/**
 * Authentication Context
 *
 * Provides Firebase auth state and feature gate checking
 */

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  User,
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { auth, googleProvider, featureFlags } from '../lib/firebase';
import { checkTesterAccess } from '../lib/testers';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  hasAccess: boolean;
  isAdmin: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  requireAuth: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [firestoreAccess, setFirestoreAccess] = useState<boolean | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);

      // Check Firestore access if user is signed in
      if (user && user.email && featureFlags.requireAuth) {
        const access = await checkTesterAccess(user.email);
        setFirestoreAccess(access);
      } else {
        setFirestoreAccess(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signInWithGoogle = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  };

  // Check if user is admin
  const isAdmin = (() => {
    if (!user || !user.email) return false;
    return featureFlags.adminEmails.includes(user.email);
  })();

  // Check if user has access
  const hasAccess = (() => {
    // If auth not required, everyone has access
    if (!featureFlags.requireAuth) return true;

    // If no user, no access
    if (!user || !user.email) return false;

    // Admins always have access
    if (isAdmin) return true;

    // If Firestore check completed, use that result
    if (firestoreAccess !== null) return firestoreAccess;

    // Fallback to env var allowlist if Firestore unavailable
    if (featureFlags.allowedTesters.length === 0) return true; // No allowlist = all authenticated users
    return featureFlags.allowedTesters.includes(user.email);
  })();

  const value = {
    user,
    loading,
    hasAccess,
    isAdmin,
    signInWithGoogle,
    signOut,
    requireAuth: featureFlags.requireAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
